import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../store/auth';
import { date } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { Drawer, Field, TextInput } from '../../components/ui/Form';

interface User {
  id: string;
  username: string;
  full_name: string;
  status: string;
  must_change_password: boolean;
  last_login_at: string | null;
  roles: string[];
  branch_ids?: string[];
}

interface Role {
  id: string;
  code: string;
  name: string;
  description: string;
}

export function UsersPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches } = useBranches();
  const myId = useAuth((st) => st.session?.user.id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [issued, setIssued] = useState<{ username: string; password: string; generated?: boolean } | null>(null);
  const [form, setForm] = useState({ username: '', fullName: '', roleCodes: [] as string[], branchIds: [] as string[] });

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ data: User[] }>('/users') });
  // Only the roles list needs role.view; a manager who can see staff but not
  // assign roles is simply not offered the list.
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<{ data: Role[] }>('/roles'),
    enabled: can('role.view'),
  });

  const close = (): void => {
    setOpen(false);
    setEditing(null);
    setForm({ username: '', fullName: '', roleCodes: [], branchIds: [] });
    setError(null);
  };

  const update = useMutation({
    mutationFn: () =>
      api.edit(`/users/${editing!.id}`, {
        fullName: form.fullName,
        // Your own roles are not sent: changing them is another admin's job.
        ...(editing!.id !== myId ? { roleCodes: form.roleCodes, branchIds: form.branchIds } : {}),
      }),
    onSuccess: () => {
      close();
      void client.invalidateQueries({ queryKey: ['users'] });
    },
    onError: setError,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ username: string; password: string }>('/users', {
        username: form.username,
        fullName: form.fullName,
        roleCodes: form.roleCodes,
        ...(form.branchIds.length > 0 ? { branchIds: form.branchIds } : {}),
      }),
    onSuccess: (result) => {
      setIssued(result);
      setOpen(false);
      setForm({ username: '', fullName: '', roleCodes: [], branchIds: [] });
      setError(null);
      void client.invalidateQueries({ queryKey: ['users'] });
    },
    onError: setError,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'DISABLED' }) =>
      api.post(`/users/${id}/status`, { status }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['users'] }),
    onError: setError,
  });

  const reset = useMutation({
    mutationFn: ({ id, password, mustChange }: { id: string; password?: string; mustChange: boolean }) =>
      api.post<{ password: string; generated: boolean }>(`/users/${id}/reset-password`, {
        ...(password ? { password } : {}),
        mustChange,
      }),
    onSuccess: (result, vars) => {
      const user = users.data?.data.find((u) => u.id === vars.id);
      setIssued({
        username: user?.username ?? 'that user',
        password: result.password,
        generated: result.generated,
      });
      setResetting(null);
    },
    onError: setError,
  });

  const [resetting, setResetting] = useState<{ id: string; username: string } | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [chosen, setChosen] = useState('');
  const [keepIt, setKeepIt] = useState(false);

  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (editing) update.mutate();
    else create.mutate();
  };

  const editingSelf = !!editing && editing.id === myId;
  const all = users.data?.data ?? [];
  const active = all.filter((u) => u.status === 'ACTIVE');
  const disabled = all.filter((u) => u.status !== 'ACTIVE');
  // Only the people who can sign in are shown; the rest are tucked away, since
  // a disabled account is kept for its history, not for daily use.
  const rows = showDisabled ? disabled : active;

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Several controls need a second person — a rate, a write-off, a discount beyond the limit. Those need somebody other than you to hold the role."
        actions={
          can('user.create') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Add user
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {resetting ? (
        <Drawer
          title={`New password for ${resetting.username}`}
          subtitle="Type one you can tell them, or leave it blank and one will be made."
          onClose={() => setResetting(null)}
          footer={
            <button
              className="btn-primary w-full"
              disabled={reset.isPending || (chosen.length > 0 && chosen.length < 10)}
              onClick={() => reset.mutate({ id: resetting.id, password: chosen || undefined, mustChange: !keepIt })}
            >
              {reset.isPending ? 'Setting…' : chosen ? 'Set this password' : 'Generate a password'}
            </button>
          }
        >
          <div className="space-y-3">
            <Field
              label="Password"
              hint="At least ten characters. Leave blank for a generated one."
              error={chosen.length > 0 && chosen.length < 10 ? 'Ten characters or more.' : null}
            >
              <TextInput value={chosen} onChange={setChosen} maxLength={128} placeholder="leave blank to generate" />
            </Field>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={keepIt} onChange={(e) => setKeepIt(e.target.checked)} className="mt-0.5" />
              <span>
                Let them keep this password
                <span className="block text-2xs text-ink-soft">
                  Otherwise they are asked to change it the first time they sign in, which is the safer default —
                  a password somebody else typed is a password somebody else knows.
                </span>
              </span>
            </label>
          </div>
        </Drawer>
      ) : null}

      {issued ? (
        <div className="mb-4 rounded border border-gold-400 bg-gold-50 p-4 text-sm">
          <p className="font-medium">
            Password for <span className="num">{issued.username}</span>: <span className="num">{issued.password}</span>
          </p>
          <p className="mt-1 text-ink-soft">
            Shown once and never again. They must change it when they first sign in.
          </p>
          <button className="btn-ghost mt-3 py-1" onClick={() => setIssued(null)}>
            I have written it down
          </button>
        </div>
      ) : null}

      {users.isLoading ? <Loading /> : null}
      {/* Active people by default; disabled ones only when asked for. */}
      {disabled.length > 0 ? (
        <div className="mb-3 flex gap-1.5">
          <button
            onClick={() => setShowDisabled(false)}
            className={`rounded-full px-3 py-1.5 text-sm ${!showDisabled ? 'bg-velvet text-white' : 'bg-silver-50 text-ink-soft hover:bg-gold-50'}`}
          >
            Active ({active.length})
          </button>
          <button
            onClick={() => setShowDisabled(true)}
            className={`rounded-full px-3 py-1.5 text-sm ${showDisabled ? 'bg-velvet text-white' : 'bg-silver-50 text-ink-soft hover:bg-gold-50'}`}
          >
            Disabled ({disabled.length})
          </button>
        </div>
      ) : null}

      {!users.isLoading && rows.length === 0 ? (
        <Empty message={showDisabled ? 'No disabled users.' : 'No active users yet.'} />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Name', 'Username', 'Roles', 'Last signed in', 'Status', '']}>
          {rows.map((u) => (
            <tr key={u.id}>
              <td className="td">{u.full_name}</td>
              <td className="td num">{u.username}</td>
              <td className="td text-xs">{u.roles.join(', ') || '—'}</td>
              <td className="td">{u.last_login_at ? date(u.last_login_at) : 'never'}</td>
              <td className="td">
                {u.status === 'ACTIVE' ? (
                  <span className="text-success">Active</span>
                ) : (
                  <span className="text-danger">Disabled</span>
                )}
                {u.must_change_password ? (
                  <span className="block text-xs text-ink-soft">must change password</span>
                ) : null}
              </td>
              <td className="td">
                <div className="flex gap-2">
                  {can('user.edit') ? (
                    <button
                      className="btn-ghost py-1 text-xs"
                      onClick={() => {
                        setEditing(u);
                        setForm({
                          username: u.username,
                          fullName: u.full_name,
                          roleCodes: u.roles,
                          branchIds: u.branch_ids ?? [],
                        });
                        setOpen(true);
                      }}
                    >
                      Edit
                    </button>
                  ) : null}
                  {can('user.reset_password') ? (
                    <button className="btn-ghost py-1 text-xs" onClick={() => {
                      setChosen('');
                      setKeepIt(false);
                      setResetting({ id: u.id, username: u.username });
                    }}>
                      Reset password
                    </button>
                  ) : null}
                  {can('user.disable') ? (
                    <button
                      className="btn-ghost py-1 text-xs"
                      onClick={() =>
                        setStatus.mutate({ id: u.id, status: u.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' })
                      }
                    >
                      {u.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title={editing ? `Edit ${editing.full_name}` : 'Add a user'}
          subtitle={
            editing
              ? 'The username stays — every action they have taken is recorded against it.'
              : "A password is generated and shown once. Nobody creating an account gets to choose somebody else's password."
          }
          onClose={close}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Full name" required>
              <TextInput value={form.fullName} onChange={(v) => setForm({ ...form, fullName: v })} autoFocus />
            </Field>

            <Field label="Username" required hint="Lowercase letters, numbers, dot, dash or underscore.">
              <TextInput
                value={form.username}
                onChange={(v) => setForm({ ...form, username: v.toLowerCase() })}
                placeholder="priya"
                disabled={!!editing}
              />
            </Field>

            <Field
              label="Roles"
              required
              hint={
                editingSelf
                  ? 'You cannot change your own roles — another administrator has to.'
                  : 'What they may do. Give the least that lets them do their job.'
              }
            >
              <div className="max-h-56 space-y-1 overflow-y-auto rounded border border-ink/15 bg-white p-2">
                {(roles.data?.data ?? []).map((r) => (
                  <label key={r.id} className="flex items-start gap-2 rounded p-1 text-sm hover:bg-gold-50">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.roleCodes.includes(r.code)}
                      disabled={editingSelf || (!!editing && !can('permission.assign'))}
                      onChange={() => setForm({ ...form, roleCodes: toggle(form.roleCodes, r.code) })}
                    />
                    <span>
                      {r.name}
                      <span className="block text-xs text-ink-soft">{r.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>

            {branches.length > 1 ? (
              <Field label="Branches" hint="Leave all unticked to give access to every branch.">
                <div className="space-y-1 rounded border border-ink/15 bg-white p-2">
                  {branches.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.branchIds.includes(b.id)}
                        onChange={() => setForm({ ...form, branchIds: toggle(form.branchIds, b.id) })}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
              </Field>
            ) : null}

            <div className="flex gap-2 pt-2">
              <button
                className="btn-primary"
                disabled={!form.username || !form.fullName || form.roleCodes.length === 0 || create.isPending || update.isPending}
              >
                {editing ? (update.isPending ? 'Saving…' : 'Save changes') : create.isPending ? 'Creating…' : 'Create user'}
              </button>
              <button type="button" className="btn-ghost" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
